/**
 * Project detail page.
 *
 * Shows all project information to the client. Allows editing details
 * as long as the contract has not been signed. After signing, all fields
 * are read-only.
 *
 * @module ProjectDetailPage
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Chip,
  Stack,
  Divider,
  Skeleton,
  alpha,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PaletteIcon from '@mui/icons-material/Palette';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LockIcon from '@mui/icons-material/Lock';
import { useAuth } from '../contexts/AuthContext';
import api, { getErrorMessage } from '../api';
import { GREEN, DARK_BLUE } from '../theme';
import { PageTransition, FloatingOrb } from '../components/AnimatedComponents';

/** Statuses where editing is still allowed. */
const EDITABLE_STATUSES = new Set(['submitted', 'accepted', 'quote_sent']);

interface Project {
  id: string;
  user_id: string;
  project_type: string;
  app_name: string;
  description: string;
  target_audience?: string | null;
  key_features?: string[] | null;
  ai_features?: string[] | null;
  design_preferences?: string[] | null;
  logo_blob_name?: string | null;
  timeline?: string | null;
  deadline?: string | null;
  budget_range?: string | null;
  additional_notes?: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Logo state
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [extractingColors, setExtractingColors] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const colorInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Edit form state
  const [form, setForm] = useState({
    app_name: '',
    description: '',
    target_audience: '',
    key_features: [''] as string[],
    ai_features: [''] as string[],
    design_preferences: ['#1a3a5c', '#39b54a'] as string[],
    timeline: '',
    deadline: '',
    budget_range: '',
    additional_notes: '',
  });

  const isEditable = project ? EDITABLE_STATUSES.has(project.status) : false;

  // Fetch project
  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    if (!id) return;
    api.get(`/api/projects/${id}`)
      .then((res) => {
        setProject(res.data);
        populateForm(res.data);
        // Auto-start editing if ?edit=true and project is editable
        if (searchParams.get('edit') === 'true' && EDITABLE_STATUSES.has(res.data.status)) {
          setEditing(true);
        }
      })
      .catch(() => navigate('/dashboard'))
      .finally(() => setLoading(false));
  }, [user, id, navigate, searchParams]);

  // Fetch logo URL
  useEffect(() => {
    if (!project?.logo_blob_name) return;
    api.get(`/api/projects/${project.id}/logo`)
      .then((res) => setLogoUrl(res.data.download_url))
      .catch(() => {});
  }, [project?.id, project?.logo_blob_name]);

  const populateForm = (p: Project) => {
    setForm({
      app_name: p.app_name || '',
      description: p.description || '',
      target_audience: p.target_audience || '',
      key_features: p.key_features?.length ? [...p.key_features] : [''],
      ai_features: p.ai_features?.length ? [...p.ai_features] : [''],
      design_preferences: p.design_preferences?.length ? [...p.design_preferences] : ['#1a3a5c', '#39b54a'],
      timeline: p.timeline || '',
      deadline: p.deadline || '',
      budget_range: p.budget_range || '',
      additional_notes: p.additional_notes || '',
    });
  };

  const handleChange = (field: string) => (e: any) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  // --- List field handlers ---
  const handleListChange = (field: 'key_features' | 'ai_features', index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => {
      const list = [...prev[field]];
      list[index] = e.target.value;
      return { ...prev, [field]: list };
    });
  };

  const addListItem = (field: 'key_features' | 'ai_features') => () => {
    setForm((prev) => ({ ...prev, [field]: [...prev[field], ''] }));
  };

  const removeListItem = (field: 'key_features' | 'ai_features', index: number) => () => {
    setForm((prev) => {
      const list = prev[field].filter((_: string, i: number) => i !== index);
      return { ...prev, [field]: list.length ? list : [''] };
    });
  };

  // --- Color handlers ---
  const handleColorChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => {
      const colors = [...prev.design_preferences];
      colors[index] = e.target.value;
      return { ...prev, design_preferences: colors };
    });
  };

  const addColor = () => {
    if (form.design_preferences.length < 5) {
      setForm((prev) => ({ ...prev, design_preferences: [...prev.design_preferences, '#cccccc'] }));
    }
  };

  const removeColor = (index: number) => () => {
    if (form.design_preferences.length > 2) {
      setForm((prev) => ({
        ...prev,
        design_preferences: prev.design_preferences.filter((_: string, i: number) => i !== index),
      }));
    }
  };

  // --- Logo upload & color extraction ---
  const extractColorsFromImage = (imageUrl: string): Promise<string[]> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const size = 100;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve([]); return; }
        ctx.drawImage(img, 0, 0, size, size);
        const data = ctx.getImageData(0, 0, size, size).data;
        const colorMap = new Map<string, number>();
        for (let i = 0; i < data.length; i += 4) {
          const r = Math.round(data[i] / 16) * 16;
          const g = Math.round(data[i + 1] / 16) * 16;
          const b = Math.round(data[i + 2] / 16) * 16;
          const a = data[i + 3];
          if (a < 128) continue;
          if (r > 240 && g > 240 && b > 240) continue;
          if (r < 15 && g < 15 && b < 15) continue;
          const hex = '#' + [r, g, b].map(c => Math.min(255, c).toString(16).padStart(2, '0')).join('');
          colorMap.set(hex, (colorMap.get(hex) || 0) + 1);
        }
        const sorted = [...colorMap.entries()].sort((a, b) => b[1] - a[1]);
        const distinct: string[] = [];
        for (const [hex] of sorted) {
          if (distinct.length >= 5) break;
          const isDifferent = distinct.every(existing => {
            const r1 = parseInt(hex.slice(1, 3), 16), g1 = parseInt(hex.slice(3, 5), 16), b1 = parseInt(hex.slice(5, 7), 16);
            const r2 = parseInt(existing.slice(1, 3), 16), g2 = parseInt(existing.slice(3, 5), 16), b2 = parseInt(existing.slice(5, 7), 16);
            return Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2) > 60;
          });
          if (isDifferent) distinct.push(hex);
        }
        while (distinct.length < 2 && sorted.length > distinct.length) {
          const next = sorted[distinct.length]?.[0];
          if (next && !distinct.includes(next)) distinct.push(next);
          else break;
        }
        resolve(distinct.length >= 2 ? distinct : []);
      };
      img.onerror = () => resolve([]);
      img.src = imageUrl;
    });
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/jpg'];
    const ext = file.name.toLowerCase().split('.').pop() || '';
    if (!allowedTypes.includes(file.type) && !['svg', 'png', 'jpg', 'jpeg'].includes(ext)) {
      setError(t('submit.logoInvalidType'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError(t('submit.logoTooLarge'));
      return;
    }
    setError('');
    setLogoFile(file);
    const previewUrl = URL.createObjectURL(file);
    setLogoPreview(previewUrl);
    if (ext !== 'svg' && file.type !== 'image/svg+xml') {
      setExtractingColors(true);
      const colors = await extractColorsFromImage(previewUrl);
      if (colors.length >= 2) {
        setForm((prev) => ({ ...prev, design_preferences: colors }));
      }
      setExtractingColors(false);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoPreview(null);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const startEditing = () => {
    if (project) populateForm(project);
    setLogoFile(null);
    setLogoPreview(null);
    setEditing(true);
    setError('');
    setSuccess('');
  };

  const cancelEditing = () => {
    if (project) populateForm(project);
    setLogoFile(null);
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoPreview(null);
    setEditing(false);
    setError('');
  };

  const saveChanges = useCallback(async () => {
    if (!project) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload: Record<string, any> = {
        app_name: form.app_name,
        description: form.description,
        target_audience: form.target_audience || null,
        key_features: form.key_features.filter(f => f.trim()),
        ai_features: form.ai_features.filter(f => f.trim()),
        design_preferences: form.design_preferences,
        timeline: form.timeline || null,
        deadline: form.deadline || null,
        budget_range: form.budget_range || null,
        additional_notes: form.additional_notes || null,
      };
      if (payload.key_features.length === 0) payload.key_features = null;
      if (payload.ai_features.length === 0) payload.ai_features = null;

      const res = await api.patch(`/api/projects/${project.id}`, payload);
      setProject(res.data);
      populateForm(res.data);

      // Upload logo if a new one is selected
      if (logoFile) {
        const formData = new FormData();
        formData.append('file', logoFile);
        try {
          await api.post(`/api/projects/${project.id}/logo`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          // Refresh logo URL
          const logoRes = await api.get(`/api/projects/${project.id}/logo`);
          setLogoUrl(logoRes.data.download_url);
        } catch {
          // non-critical
        }
      }

      setEditing(false);
      setSuccess(t('projectDetail.saved'));
    } catch (err: any) {
      setError(getErrorMessage(err, t('projectDetail.saveFailed')));
    } finally {
      setSaving(false);
    }
  }, [project, form, logoFile, t]);

  // --- Timeline options (same logic as SubmitProjectPage) ---
  const getTimelineOptions = (): { value: string; label: string }[] => {
    const projectType = project?.project_type || '';
    const hasAi = form.ai_features.some(f => f.trim() !== '');
    const aiCount = form.ai_features.filter(f => f.trim() !== '').length;
    const isBoth = projectType === 'both';

    if (isBoth && hasAi) {
      return [
        { value: '1-2 months', label: t('submit.timeline12Months') },
        { value: '2-3 months', label: t('submit.timeline3') },
        { value: '3-6 months', label: t('submit.timeline4') },
        { value: 'flexible', label: t('submit.timeline5') },
      ];
    }
    if (isBoth || (hasAi && aiCount >= 2)) {
      return [
        { value: '3-4 weeks', label: t('submit.timeline34Weeks') },
        { value: '1-2 months', label: t('submit.timeline12Months') },
        { value: '2-3 months', label: t('submit.timeline3') },
        { value: '3-6 months', label: t('submit.timeline4') },
        { value: 'flexible', label: t('submit.timeline5') },
      ];
    }
    if (hasAi && aiCount === 1) {
      return [
        { value: '3-4 weeks', label: t('submit.timeline34Weeks') },
        { value: '1-2 months', label: t('submit.timeline12Months') },
        { value: '2-3 months', label: t('submit.timeline3') },
        { value: '3-6 months', label: t('submit.timeline4') },
        { value: 'flexible', label: t('submit.timeline5') },
      ];
    }
    return [
      { value: '1-2 weeks', label: t('submit.timeline1') },
      { value: '3-4 weeks', label: t('submit.timeline34Weeks') },
      { value: '1-2 months', label: t('submit.timeline12Months') },
      { value: '2-3 months', label: t('submit.timeline3') },
      { value: '3-6 months', label: t('submit.timeline4') },
      { value: 'flexible', label: t('submit.timeline5') },
    ];
  };

  if (!user) return null;

  // --- Render helpers ---
  const renderReadOnlyField = (label: string, value: string | null | undefined) => {
    if (!value) return null;
    return (
      <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(DARK_BLUE, 0.02), border: `1px solid ${alpha(DARK_BLUE, 0.06)}` }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontWeight: 500, mt: 0.5 }}>{value}</Typography>
      </Box>
    );
  };

  const renderReadOnlyList = (label: string, items: string[] | null | undefined) => {
    if (!items?.length) return null;
    return (
      <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(DARK_BLUE, 0.02), border: `1px solid ${alpha(DARK_BLUE, 0.06)}` }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
          {label}
        </Typography>
        <Stack spacing={0.5}>
          {items.map((item, idx) => (
            <Stack key={idx} direction="row" spacing={1} alignItems="center">
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: GREEN, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{item}</Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    );
  };

  const renderReadOnlyColors = (label: string, colors: string[] | null | undefined) => {
    if (!colors?.length) return null;
    return (
      <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(DARK_BLUE, 0.02), border: `1px solid ${alpha(DARK_BLUE, 0.06)}` }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
          {label}
        </Typography>
        <Stack direction="row" spacing={1.5}>
          {colors.map((color, idx) => (
            <Tooltip key={idx} title={color}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    bgcolor: color,
                    border: '2px solid',
                    borderColor: alpha('#000', 0.12),
                  }}
                />
                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'text.secondary' }}>
                  {color}
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Stack>
      </Box>
    );
  };

  return (
    <PageTransition>
      <SEO
        title={project?.app_name || t('projectDetail.title')}
        description={t('projectDetail.seoDesc')}
        canonical={`/projects/${id}`}
        noindex
      />
      <Box
        sx={{
          py: { xs: 3, md: 5 },
          position: 'relative',
          overflow: 'hidden',
          minHeight: '80vh',
          background: `linear-gradient(180deg, ${alpha(GREEN, 0.02)} 0%, transparent 40%)`,
        }}
      >
        <FloatingOrb size={300} color={GREEN} top={-80} right={-100} />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/dashboard')}
              sx={{ mb: 3, textTransform: 'none' }}
            >
              {t('projectDetail.backToDashboard')}
            </Button>
          </motion.div>

          {loading ? (
            <Paper sx={{ p: 4 }}>
              <Skeleton variant="text" width="40%" height={40} />
              <Skeleton variant="text" width="70%" sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            </Paper>
          ) : !project ? null : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  boxShadow: '0 8px 40px rgba(0,0,0,0.06)',
                  border: `1px solid ${alpha(DARK_BLUE, 0.06)}`,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${GREEN}, ${DARK_BLUE})` }} />

                {/* Title row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>{project.app_name}</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Chip
                        label={project.project_type === 'both' ? t('submit.typeBoth') : project.project_type === 'app' ? t('submit.typeApp') : t('submit.typeWebsite')}
                        size="small"
                        color="primary"
                      />
                      <Chip
                        label={t(`status.${project.status.replace('_', '-')}`, project.status.replace('_', ' '))}
                        size="small"
                        color="secondary"
                      />
                    </Stack>
                  </Box>
                  {isEditable && !editing && (
                    <Button
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={startEditing}
                      sx={{ borderRadius: 3 }}
                    >
                      {t('projectDetail.edit')}
                    </Button>
                  )}
                  {!isEditable && (
                    <Tooltip title={t('projectDetail.lockedTooltip')}>
                      <Chip
                        icon={<LockIcon />}
                        label={t('projectDetail.locked')}
                        size="small"
                        variant="outlined"
                        color="default"
                      />
                    </Tooltip>
                  )}
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                {/* ========== READ-ONLY VIEW ========== */}
                {!editing && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {renderReadOnlyField(t('submit.reviewDescription'), project.description)}
                    {renderReadOnlyField(t('submit.reviewAudience'), project.target_audience)}
                    {renderReadOnlyList(t('submit.reviewFeatures'), project.key_features)}
                    {renderReadOnlyList(t('submit.reviewAi'), project.ai_features)}

                    {/* Logo */}
                    {(logoUrl || project.logo_blob_name) && (
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(DARK_BLUE, 0.02), border: `1px solid ${alpha(DARK_BLUE, 0.06)}` }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
                          {t('submit.reviewLogo')}
                        </Typography>
                        {logoUrl && (
                          <Box sx={{ width: 80, height: 80, borderRadius: 2, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', border: `2px solid ${alpha(GREEN, 0.2)}` }}>
                            <img src={logoUrl} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                          </Box>
                        )}
                      </Box>
                    )}

                    {renderReadOnlyColors(t('submit.reviewDesign'), project.design_preferences)}
                    {renderReadOnlyField(t('submit.reviewTimeline'), project.timeline)}
                    {renderReadOnlyField(t('submit.reviewDeadline'), project.deadline)}
                    {renderReadOnlyField(t('submit.reviewBudget'), project.budget_range)}
                    {renderReadOnlyField(t('submit.reviewNotes'), project.additional_notes)}

                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      {t('projectDetail.lastUpdated', { date: new Date(project.updated_at).toLocaleString() })}
                    </Typography>
                  </Box>
                )}

                {/* ========== EDIT VIEW ========== */}
                {editing && (
                  <Box>
                    {/* App Name */}
                    <TextField
                      fullWidth
                      label={t('submit.appName')}
                      value={form.app_name}
                      onChange={handleChange('app_name')}
                      required
                      sx={{ mb: 3 }}
                    />

                    {/* Description */}
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label={t('submit.description')}
                      helperText={t('submit.descriptionHelp')}
                      value={form.description}
                      onChange={handleChange('description')}
                      required
                      sx={{ mb: 3 }}
                    />

                    {/* Target Audience */}
                    <TextField
                      fullWidth
                      label={t('submit.targetAudience')}
                      helperText={t('submit.targetAudienceHelp')}
                      value={form.target_audience}
                      onChange={handleChange('target_audience')}
                      sx={{ mb: 3 }}
                    />

                    <Divider sx={{ mb: 3 }} />

                    {/* Key Features — dynamic list */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      {t('submit.keyFeatures')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {t('submit.keyFeaturesHelp')}
                    </Typography>
                    {form.key_features.map((feature, idx) => (
                      <Stack key={idx} direction="row" spacing={1} sx={{ mb: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder={`${t('submit.featurePlaceholder')} ${idx + 1}`}
                          value={feature}
                          onChange={handleListChange('key_features', idx)}
                        />
                        <IconButton
                          size="small"
                          onClick={removeListItem('key_features', idx)}
                          disabled={form.key_features.length <= 1 && !feature}
                          sx={{ color: 'text.secondary' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addListItem('key_features')}
                      sx={{ mb: 3, textTransform: 'none' }}
                    >
                      {t('submit.addFeature')}
                    </Button>

                    <Divider sx={{ mb: 3 }} />

                    {/* AI Features — dynamic list */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      {t('submit.aiFeatures')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {t('submit.aiFeaturesHelp')}
                    </Typography>
                    {form.ai_features.map((feature, idx) => (
                      <Stack key={idx} direction="row" spacing={1} sx={{ mb: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder={`${t('submit.aiFeaturePlaceholder')} ${idx + 1}`}
                          value={feature}
                          onChange={handleListChange('ai_features', idx)}
                        />
                        <IconButton
                          size="small"
                          onClick={removeListItem('ai_features', idx)}
                          disabled={form.ai_features.length <= 1 && !feature}
                          sx={{ color: 'text.secondary' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addListItem('ai_features')}
                      sx={{ mb: 3, textTransform: 'none' }}
                    >
                      {t('submit.addAiFeature')}
                    </Button>

                    <Divider sx={{ mb: 3 }} />

                    {/* Logo Upload */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      {t('submit.logoUpload')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {t('submit.logoUploadHelp')}
                    </Typography>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept=".svg,.png,.jpg,.jpeg"
                      style={{ display: 'none' }}
                      onChange={handleLogoUpload}
                    />
                    {(logoPreview || logoUrl) ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 2,
                            border: `2px solid ${alpha(GREEN, 0.3)}`,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'background.default',
                          }}
                        >
                          <img
                            src={logoPreview || logoUrl || ''}
                            alt="Logo preview"
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{logoFile?.name || t('projectDetail.currentLogo')}</Typography>
                          {logoPreview && (
                            <Button size="small" color="error" onClick={removeLogo} sx={{ textTransform: 'none', mt: 0.5 }}>
                              {t('submit.removeLogo')}
                            </Button>
                          )}
                          <Button
                            size="small"
                            onClick={() => logoInputRef.current?.click()}
                            sx={{ textTransform: 'none', mt: 0.5, ml: 1 }}
                          >
                            {t('projectDetail.changeLogo')}
                          </Button>
                        </Box>
                        {extractingColors && (
                          <Chip label={t('submit.extractingColors')} size="small" color="secondary" />
                        )}
                      </Box>
                    ) : (
                      <Button
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => logoInputRef.current?.click()}
                        sx={{ mb: 3, textTransform: 'none', borderRadius: 2 }}
                      >
                        {t('submit.selectLogo')}
                      </Button>
                    )}

                    <Divider sx={{ mb: 3 }} />

                    {/* Design Preferences — Color picker */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PaletteIcon fontSize="small" />
                      {t('submit.designPreferences')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t('submit.designPreferencesHelp')}
                    </Typography>
                    <Stack spacing={1.5} sx={{ mb: 1 }}>
                      {form.design_preferences.map((color, idx) => (
                        <Stack key={idx} direction="row" alignItems="center" spacing={1.5}>
                          <Tooltip title={t('submit.clickToPickColor')}>
                            <Box
                              onClick={() => colorInputRefs.current[idx]?.click()}
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 2,
                                bgcolor: color,
                                border: '2px solid',
                                borderColor: alpha('#000', 0.12),
                                cursor: 'pointer',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'scale(1.1)' },
                                flexShrink: 0,
                              }}
                            />
                          </Tooltip>
                          <input
                            ref={el => { colorInputRefs.current[idx] = el; }}
                            type="color"
                            value={color}
                            onChange={handleColorChange(idx)}
                            style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                          />
                          <TextField
                            size="small"
                            value={color}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (/^#[0-9a-fA-F]{0,6}$/.test(val) || val === '') {
                                handleColorChange(idx)({ target: { value: val } } as any);
                              }
                            }}
                            sx={{ width: 130 }}
                            inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
                            {(() => {
                              const r = parseInt(color.slice(1, 3), 16) || 0;
                              const g = parseInt(color.slice(3, 5), 16) || 0;
                              const b = parseInt(color.slice(5, 7), 16) || 0;
                              return `RGB(${r}, ${g}, ${b})`;
                            })()}
                          </Typography>
                          {form.design_preferences.length > 2 && (
                            <IconButton size="small" onClick={removeColor(idx)} sx={{ color: 'text.secondary' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                    {form.design_preferences.length < 5 && (
                      <Button size="small" startIcon={<AddIcon />} onClick={addColor} sx={{ mb: 3, textTransform: 'none' }}>
                        {t('submit.addColor')}
                      </Button>
                    )}
                    {form.design_preferences.length >= 5 && <Box sx={{ mb: 3 }} />}

                    <Divider sx={{ mb: 3 }} />

                    {/* Timeline */}
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>{t('submit.timeline')}</InputLabel>
                      <Select
                        value={form.timeline}
                        label={t('submit.timeline')}
                        onChange={handleChange('timeline')}
                      >
                        {getTimelineOptions().map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Deadline */}
                    <TextField
                      fullWidth
                      label={t('submit.deadline')}
                      helperText={t('submit.deadlineHelp')}
                      type="date"
                      value={form.deadline}
                      onChange={handleChange('deadline')}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: new Date().toISOString().split('T')[0] }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarTodayIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 3 }}
                    />

                    <Divider sx={{ mb: 3 }} />

                    {/* Budget */}
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>{t('submit.budget')}</InputLabel>
                      <Select
                        value={form.budget_range}
                        label={t('submit.budget')}
                        onChange={handleChange('budget_range')}
                      >
                        <MenuItem value="< €5,000">{t('submit.budget1')}</MenuItem>
                        <MenuItem value="€5,000 - €15,000">{t('submit.budget2')}</MenuItem>
                        <MenuItem value="€15,000 - €50,000">{t('submit.budget3')}</MenuItem>
                        <MenuItem value="> €50,000">{t('submit.budget4')}</MenuItem>
                        <MenuItem value="discuss">{t('submit.budget5')}</MenuItem>
                      </Select>
                    </FormControl>

                    {/* Additional Notes */}
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label={t('submit.additionalNotes')}
                      value={form.additional_notes}
                      onChange={handleChange('additional_notes')}
                      sx={{ mb: 3 }}
                    />

                    {/* Save / Cancel buttons */}
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        onClick={cancelEditing}
                        disabled={saving}
                        sx={{ borderRadius: 3, textTransform: 'none' }}
                      >
                        {t('projectDetail.cancel')}
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={saveChanges}
                        disabled={saving || !form.app_name || !form.description}
                        sx={{ borderRadius: 3, textTransform: 'none' }}
                      >
                        {saving ? t('projectDetail.saving') : t('projectDetail.save')}
                      </Button>
                    </Stack>
                  </Box>
                )}
              </Paper>
            </motion.div>
          )}
        </Container>
      </Box>
    </PageTransition>
  );
}
